import React, { useState } from "react";
import { Avatar, Box, Typography, Tooltip } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

type NotificationCardProps = {
  avatarUrl?: string;
  senderName: string;
  subject: string;
  message: string;
  date: string;
  time: string;
  isRead: boolean;
  onClick?: () => void;
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  avatarUrl,
  senderName,
  subject,
  message,
  date,
  time,
  isRead,
  onClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded((prev) => !prev);

  const handleClick = () => {
    if (onClick) onClick();
    toggleExpanded();
  };

  const maxLength = 200;
  const shouldTruncate = message.length > maxLength;
  const displayedMessage =
    expanded || !shouldTruncate ? message : `${message.slice(0, maxLength)}...`;

  return (
    <Box
      className="notification-card"
      display="flex"
      gap={2}
      alignItems="flex-start"
      p={isRead ? 2.5 : 2}
      borderRadius={2}
      minHeight={isRead ? 100 : 90}
      sx={{
        backgroundColor: isRead ? "#1E1C2B" : "#2A273A",
        border: isRead ? "1px solid #2F2D40" : "1px solid #3F3B52",
        boxShadow: isRead ? "0 0 8px rgba(255, 255, 255, 0.05)" : "none",
        ":hover": {},
        cursor: "pointer",
        transition: "all 0.3s ease",
      }}
      onClick={handleClick}
    >
      <Avatar
        src={avatarUrl}
        alt={senderName}
        sx={{ width: 42, height: 42, mt: 0.5 }}
      />
      <Box flex={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            fontWeight={isRead ? 600 : 700}
            color="white"
            fontSize="0.95rem"
          >
            {senderName}
          </Typography>
          <Typography variant="caption" color="gray">
            {date} • {time}
          </Typography>
        </Box>

        <Typography
          fontWeight={500}
          color={isRead ? "#B0B0B0" : "#E0E0E0"}
          fontSize="0.9rem"
          mt={0.5}
        >
          {subject}
        </Typography>

        <Typography
          variant="body2"
          color="gray"
          mt={0.5}
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: expanded ? "unset" : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            transition: "all 0.3s ease",
          }}
        >
          {displayedMessage}
        </Typography>

        {shouldTruncate && (
          <Typography
            variant="caption"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
            sx={{
              cursor: "pointer",
              mt: 0.5,
              color: "#B8F890",
              fontWeight: 500,
              display: "inline-block",
              transition: "color 0.3s",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {expanded ? "Show Less" : "Read More"}
          </Typography>
        )}
      </Box>

      {!isRead && (
        <Tooltip title="Unread">
          <FiberManualRecordIcon color="error" sx={{ fontSize: 10, mt: 0.5 }} />
        </Tooltip>
      )}
    </Box>
  );
};

export default NotificationCard;
